import * as React from "react";
import * as ReactRedux from "react-redux";

import Lab from "../components/Lab";
import DocContent from "./DocContent";
import DocNavigator from "./DocNavigator";
import * as Media from "react-media";

import { selectors as labSelectors, actions as labActions, LabExample } from "../reducers/lab";
import { selectors as userSelectors } from "../reducers/current_user";
import { selectors as docPageSelectors, actions as docPageActions } from "../reducers/doc_page";

import flatMapAst from "../utils/flat_map_ast";
import { getHeaders } from "./WithHeaders";

import Drawer from "material-ui/Drawer";

require("../styles/example-theme.css");

interface DocPageProps {
	title: string;
	htmlAst: any;
	otherDocs: any[];
	locationPath: string;
	selectedGroupId: string;
	groups: Array<{
		id: string;
		title: string;
	}>;

	apiverb?: string;
	apiurl?: string;
	isLoggedIn: boolean;
	isFullscreen: boolean;
	isDrawerOpen: boolean;
	apiauthtype?: string;
	category: string;

	isLabOpen: boolean;
	selectedAppId: string;

	openExampleInLab(example: LabExample);
	openDrawer();
	closeDrawer();
}

class DocPage extends React.Component<DocPageProps> {
	generateDocUrl(docFrontmatter: any) {
		if (docFrontmatter.category === "reference") {
			return (
				"/docs/" +
				docFrontmatter.category +
				"/v" +
				docFrontmatter.version +
				"/" +
				docFrontmatter.platform +
				docFrontmatter.path
			);
		} else if (docFrontmatter.category === "core") {
			return "/docs/" + docFrontmatter.category + docFrontmatter.path;
		} else if (docFrontmatter.category === "tutorial") {
			return "/docs/" + docFrontmatter.category + docFrontmatter.path;
		} else {
			throw new Error(`DocPage: unsupported doc category: "${this.props.category}"`);
		}
	}

	render() {
		// add additional props to TryIt "example" elements

		const htmlAst = flatMapAst(this.props.htmlAst, child => {
			if (
				child.type === "element" &&
				child.tagName === "example" &&
				child.properties.tryit !== "false"
			) {
				child.properties = {
					...child.properties,
					title: this.props.title,
					apiurl: this.props.apiurl,
					apiverb: this.props.apiverb,
					apiauthtype: this.props.apiauthtype,
					authType: this.props.apiauthtype || "none",
					openInLab: this.props.openExampleInLab.bind(this),
				};
			}

			if (child.type === "element" && child.tagName === "http-headers") {
				child.properties = {
					...child.properties,
					apiverb: this.props.apiverb,
					apiauthtype: this.props.apiauthtype,
				};
			}

			return child;
		});

		const selectedDoc = this.props.otherDocs.find(doc => {
			const locationComponents: string[] = this.props.locationPath.split("/");

			let docLink: string;

			if (this.props.category === "reference") {
				docLink =
					"/docs/" +
					doc.frontmatter.category +
					"/v" +
					doc.frontmatter.version +
					"/" +
					doc.frontmatter.platform +
					doc.frontmatter.path;
			} else if (this.props.category === "core") {
				docLink = "/docs/" + doc.frontmatter.category + doc.frontmatter.path;
			} else if (this.props.category === "tutorial") {
				docLink = "/docs/" + doc.frontmatter.category + doc.frontmatter.path;
			} else {
				throw new Error(`DocPage: unsupported doc category: "${this.props.category}"`);
			}

			// remove trailing slash if exists
			return (
				(this.props.locationPath.charAt(this.props.locationPath.length - 1) === "/"
					? this.props.locationPath.substring(0, this.props.locationPath.length - 1)
					: this.props.locationPath) === docLink
			);
		});

		if (selectedDoc === undefined) {
			console.error(
				`error: could not find selected document with url "${this.props.locationPath}"`,
				this.props.otherDocs
			);
		}

		const { isLabOpen, isFullscreen, isDrawerOpen, closeDrawer, openDrawer } = this.props;

		return (
			<Media query={{ maxWidth: 768 }}>
				{isMobile => (
					<Media query={{ maxWidth: 1440 }}>
						{hideNavWhenLabOpen => (
							<div>
								{((hideNavWhenLabOpen && isLabOpen) || isMobile) && (
									<Drawer
										open={isDrawerOpen}
										width={245}
										docked={false}
										onRequestChange={closeDrawer}
										children={
											<DocNavigator
												otherDocs={this.props.otherDocs}
												locationPath={this.props.locationPath}
												groups={this.props.groups}
												shouldShowPlatforms={this.props.category === "reference"}
												selectedDoc={selectedDoc}
												generateDocUrl={this.generateDocUrl.bind(this)}
												closeDrawer={closeDrawer}
												style={{
													top: 0,
													left: 0,
													bottom: 0,
												}}
												isInDrawer
											/>
										}
									/>
								)}

								{(!hideNavWhenLabOpen || (hideNavWhenLabOpen && !isLabOpen)) &&
									!isMobile && (
										<DocNavigator
											otherDocs={this.props.otherDocs}
											locationPath={this.props.locationPath}
											groups={this.props.groups}
											shouldShowPlatforms={this.props.category === "reference"}
											selectedDoc={selectedDoc}
											generateDocUrl={this.generateDocUrl.bind(this)}
											closeDrawer={closeDrawer}
											style={{
												width: "245px",
												top: "5rem",
												left: 0,
												bottom: "2rem",
												zIndex: 2,
											}}
										/>
									)}

								<div
									className="row"
									style={{
										position: "relative",
										padding: isMobile || (hideNavWhenLabOpen && isLabOpen) ? 0 : "0 0 0 245px",
										marginRight: 0,
									}}
								>
									<div
										className={`col-12 ${isLabOpen ? "col-md-6" : "col-md-12"}`}
										style={{
											paddingRight: isMobile || !isLabOpen ? 0 : "15px",
										}}
									>
										<DocContent
											isLabOpen={isLabOpen}
											isLoggedIn={this.props.isLoggedIn}
											hasDrawer={(hideNavWhenLabOpen && isLabOpen) || isMobile}
											isDrawerOpen={isDrawerOpen}
											title={this.props.title}
											group={this.props.groups.find(g => g.id === this.props.selectedGroupId)}
											htmlAst={this.props.htmlAst}
											apiverb={this.props.apiverb}
											apiurl={this.props.apiurl}
											style={{
												maxWidth: !isLabOpen ? "820px" : "100%",
											}}
										/>
									</div>

									{!isLabOpen ||
									process.env.FEATURE_USER_LAB_ENABLED !== "true" ||
									isMobile ? null : (
										<div className="col-6">
											<Lab
												style={{
													position: "fixed",
													top: isFullscreen ? "0" : "5rem",
													width: isFullscreen
														? "100%"
														: hideNavWhenLabOpen && isLabOpen
															? "calc(50% + 23px)"
															: "calc(50% - 100px)",
													zIndex: 999,
													right: 0,
													bottom: 32,
													backgroundColor: "#445a64",
													display: "flex",
													flexDirection: "column",
												}}
											/>
										</div>
									)}
								</div>
							</div>
						)}
					</Media>
				)}
			</Media>
		);
	}
}

const mapStateToProps = state => ({
	isLabOpen: labSelectors.selectIsOpen(state),
	isFullscreen: labSelectors.selectIsFullscreen(state),
	selectedAppId: userSelectors.selectAppId(state),
	isLoggedIn: userSelectors.selectIsLoggedIn(state),
	isDrawerOpen: docPageSelectors.isDrawerOpen(state),
});

const mapDispatchToProps = {
	openExampleInLab: labActions.open,
	openDrawer: docPageActions.openDrawer,
	closeDrawer: docPageActions.closeDrawer,
};

export default ReactRedux.connect(
	mapStateToProps,
	mapDispatchToProps
)(DocPage);



// WEBPACK FOOTER //
// ./src/components/DocPage.tsx